import { useCallback, useRef, useState } from 'react';
import { getSession, setSession, needsStoredSessionReauthentication } from '../auth.js';
import { AdminSessionLifecycle } from '../session-lifecycle.js';
import type { AdminHttpClient } from '../http.js';
import type { AdminSession } from '../types.js';

interface UseAdminSessionOptions {
  onLoggedIn?: (session: AdminSession) => void;
  onExpired?: () => void;
}

export function useAdminSession(options: UseAdminSessionOptions = {}) {
  const [session, setSessionState] = useState<AdminSession | undefined>(() => getSession());
  const [notice, setNotice] = useState<string | undefined>(() => needsStoredSessionReauthentication() ? 'session_reauthentication_required' : undefined);
  const sessionRef = useRef(session);
  const optionsRef = useRef(options); optionsRef.current = options;
  const lifecycleRef = useRef<AdminSessionLifecycle>();
  if (!lifecycleRef.current) lifecycleRef.current = new AdminSessionLifecycle(
    () => sessionRef.current,
    next => {
      // A failed write cannot publish a successor in memory. Clearing always clears memory.
      if (next) { setSession(next); sessionRef.current = next; setSessionState(next); }
      else { sessionRef.current = undefined; setSessionState(undefined); try { setSession(undefined); } catch { /* memory is already clear */ } }
    },
    () => { setNotice('session_reauthentication_required'); optionsRef.current.onExpired?.(); },
  );
  const lifecycle = lifecycleRef.current;
  const persistSession = useCallback((next: AdminSession | undefined) => lifecycle.updateContext(next), [lifecycle]);
  const expireSession = useCallback(() => lifecycle.clear(), [lifecycle]);
  const beginLogin = useCallback(() => { setNotice(undefined); return lifecycle.beginLogin(); }, [lifecycle]);
  const onLoggedIn = useCallback((next: AdminSession, generation: number) => {
    lifecycle.acceptLogin(next, generation); setNotice(undefined); optionsRef.current.onLoggedIn?.(next);
  }, [lifecycle]);
  const onLogout = useCallback(async (http: AdminHttpClient) => {
    const pending = lifecycle.logout(http);
    const generation = lifecycle.generation;
    setNotice('session_logout_unconfirmed');
    const result = await pending;
    if (generation === lifecycle.generation) setNotice(`session_logout_${result}`);
  }, [lifecycle]);
  return { session, sessionRef, persistSession, expireSession, beginLogin, onLoggedIn, onLogout, lifecycle, notice };
}
